'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/shared/lib/cn';

interface ColorGroup {
  nameKey: string;
  colors: string[];
}

/**
 * Architectural color palette matching Android/Apple implementations.
 * Four groups: Whites & Grays, Warm Tones, Cool Tones, Markers
 */
const COLOR_GROUPS: ColorGroup[] = [
  {
    nameKey: 'whitesAndGrays',
    colors: [
      '#000000',
      '#333333',
      '#666666',
      '#808080',
      '#999999',
      '#CCCCCC',
      '#FFFFFF',
    ],
  },
  {
    nameKey: 'warmTones',
    colors: [
      '#F5DEB3',
      '#DEB887',
      '#C2996B',
      '#8B4513',
      '#CC8540',
      '#B87333',
      '#8B5A2B',
    ],
  },
  {
    nameKey: 'coolTones',
    colors: [
      '#ADD8E6',
      '#87CEEB',
      '#4682B4',
      '#99CC99',
      '#6B8E6B',
      '#556B55',
      '#455945',
    ],
  },
  {
    nameKey: 'markers',
    colors: ['#39FF14', '#FFEA00', '#FF073A'],
  },
];

interface DrawingColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  overlayClassName?: string;
}

export function DrawingColorPicker({
  color,
  onChange,
  open,
  onOpenChange,
  className,
  overlayClassName,
}: DrawingColorPickerProps) {
  const t = useTranslations('control.editor');
  const tCommon = useTranslations('common');
  const [tempColor, setTempColor] = useState(color);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const handleSelect = (selectedColor: string) => {
    onChange(selectedColor);
  };

  const handleCustomApply = () => {
    onChange(tempColor);
    setShowCustomPicker(false);
  };

  /**
   * Compute luminance from hex to determine if a color is "light".
   */
  function isLightColor(hex: string): boolean {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return 0.299 * r + 0.587 * g + 0.114 * b > 0.7;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'w-full max-w-[calc(100%-2rem)] sm:max-w-sm max-h-[90vh] p-0 overflow-hidden',
          '!top-[50%] !translate-y-[-50%]',
          className
        )}
        overlayClassName={overlayClassName}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{t('colorPickerTitle')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('chooseColor')}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-140px)] overscroll-contain">
          <div className="px-6 py-4 space-y-4">
            {COLOR_GROUPS.map((group) => (
              <div key={group.nameKey} className="space-y-1.5">
                <Label className="text-list-item-meta text-muted-foreground">
                  {t(group.nameKey)}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {group.colors.map((hex) => {
                    const isSelected =
                      color.toUpperCase() === hex.toUpperCase();
                    const light = isLightColor(hex);
                    return (
                      <button
                        key={hex}
                        onClick={() => handleSelect(hex)}
                        className={cn(
                          'w-9 h-9 rounded-full transition-all border-2',
                          isSelected
                            ? 'border-primary scale-110 ring-2 ring-primary/30'
                            : light
                              ? 'border-black/20 hover:scale-105'
                              : 'border-transparent hover:scale-105'
                        )}
                        style={{ backgroundColor: hex }}
                        title={hex}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Custom color */}
            <div className="space-y-1.5">
              <Label className="text-list-item-meta text-muted-foreground">
                {t('customColor')}
              </Label>
              {showCustomPicker ? (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={tempColor}
                    onChange={(e) => setTempColor(e.target.value)}
                    className="w-12 h-9 rounded-lg border border-border cursor-pointer"
                    style={{
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none',
                      backgroundColor: tempColor,
                    }}
                  />
                  <span className="text-list-item-subtitle text-muted-foreground font-mono">
                    {tempColor.toUpperCase()}
                  </span>
                  <Button size="sm" onClick={handleCustomApply}>
                    {tCommon('apply')}
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setTempColor(color);
                    setShowCustomPicker(true);
                  }}
                  className="w-9 h-9 rounded-full border-2 border-dashed border-muted-foreground/50 hover:border-primary transition-colors flex items-center justify-center text-muted-foreground"
                  title={t('customColor')}
                >
                  +
                </button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            {tCommon('close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
